/**
 * Actycrea: swiper.esm.js (ES6)
 * MIT
 */

import Swiper from 'swiper';
import { Pagination, Autoplay, Navigation, EffectCards, Mousewheel , Grid } from 'swiper/modules';
import { PREFIX } from '../helpers/config.js';
import { deepMerge } from '../helpers/helpers.js';
import { getCurrentBreakpoint } from '../helpers/breakpoints.js';
import { onResizeEnd , onActive , onSlideChange } from '../helpers/events.js';

// Selectores con PREFIX + fallback legado
const SELECTOR = `data-${PREFIX}-mysw`
const SELECTOR_DATA = `[${SELECTOR}]`;
const SELECTOR_PAGINATION = `[data-${PREFIX}-pagination]`;
const SELECTOR_SWIPER = `[data-${PREFIX}-swiper]`;
const SELECTOR_NEXT = `[data-${PREFIX}-swiper-next]`;
const SELECTOR_PREV = `[data-${PREFIX}-swiper-prev]`;
const SELECTOR_SLIDE = `[data-${PREFIX}-slide]`;
const DATA_KEY_SLIDE   = `${PREFIX}Slide`;

const SELECTOR_AVATAR = `data-${PREFIX}-avatar`;
const SELECTOR_YEAR = `data-${PREFIX}-year`;

const CLASS_ACTIVE_NAV = 'active';

const DEFAULTS = {
  pagination: false,
  navigation: false,
  type: '',
  merge: {},
  modules: {
    pagination: {
      el: SELECTOR_PAGINATION,
      clickable: true,
      type: 'fraction',
    },
    navigation: {
      hideOnClick: true,
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    }
  },
  params: {
    modules: [ Navigation, Pagination, Autoplay, EffectCards, Mousewheel, Grid ],
    speed: 800,
    slidesPerView: 1,
    spaceBetween: 0,
    autoplay: { delay: 5000 }
  }
};

export class MySW {
  constructor( element, config = {} ) {
    if ( !( element instanceof HTMLElement ) ) throw new Error( 'MySW: element inválido' );

    this._element = element;
    this._config = deepMerge( DEFAULTS, config );

    this._prevEl = element.querySelector( SELECTOR_PREV );
    this._nextEl = element.querySelector( SELECTOR_NEXT );

    this.swiperEl = element.querySelector( SELECTOR_SWIPER );

    this.avatarEl = null;
    this.yearsEl = null;

    let params_ = { ...this._config.params };

    // Paginación
    if ( this._config.pagination ) {
      params_.pagination = { ...this._config.modules.pagination };
      params_.pagination.el = element.querySelector( params_.pagination.el );
      if ( params_.pagination.type == 'fraction' ) {
        params_.pagination.renderFraction = ( currentClass, totalClass ) => {
          return '<span class="' + currentClass + '"></span>' +
            ' · ' +
            '<span class="' + totalClass + '"></span>';
        }
      }
      this._config.pagination = false;
    }

    // Navegación
    if ( this._config.navigation ) {
      params_.navigation = { ...this._config.modules.navigation };
      params_.navigation.nextEl = element.querySelector( params_.navigation.nextEl ) || element.querySelector( SELECTOR_NEXT );
      params_.navigation.prevEl = element.querySelector( params_.navigation.prevEl ) || element.querySelector( SELECTOR_PREV );
      this._config.navigation = false;
    }

    let merge_ = this._config.merge;
    if ( typeof merge_ === 'string' ) {
      try { merge_ = JSON.parse( merge_ ); } catch ( e ) { merge_ = {}; }
    }

    const config_ = deepMerge( params_, merge_ );

    console.log( 'MySW: => constructor => config_ ' , config_ );

    config_.on = { init: ( swiper ) => this._init( swiper ) };
    this._swiper = new Swiper( this.swiperEl, config_ );

    this._bind( );
  }

  _bind( ) {
    this._prevEl?.addEventListener( 'click', ( event ) => this._prevSlide( event ), false );
    this._nextEl?.addEventListener( 'click', ( event ) => this._nextSlide( event ), false );
    window.addEventListener( onResizeEnd, ( event ) => this._refresh( event ), false );
    if( this._config.type == 'contact' ){
      this._map = this._map.bind( this );
      this._element.addEventListener( 'click' , this._map , false );
    }
  }

  _init( swiper ) {
    this._createAvatarNavigation( swiper );
    this._createYearNavigation( swiper );
    swiper.on( 'slideChange', ( swiper ) => this._slideChange( swiper ) );
    if( this._config.type == 'contact' ){
      setTimeout( ( ) => {
        this._map({ target: this._element.querySelector( SELECTOR_SLIDE ) });
      } , 500 );
    }
  }

  _slideChange( swiper ) {
    if ( this.avatarEl !== null && this.avatarEl.childNodes[ swiper.activeIndex ] ) {
      this.avatarEl.childNodes.forEach( el => el.classList.remove( CLASS_ACTIVE_NAV ) );
      this.avatarEl.childNodes[ swiper.activeIndex ].classList.add( CLASS_ACTIVE_NAV );
    }
    if ( this.yearsEl !== null && this.yearsEl.childNodes[ swiper.activeIndex ] ) {
      this.yearsEl.childNodes.forEach( el => el.classList.remove( CLASS_ACTIVE_NAV ) );
      this.yearsEl.childNodes[ swiper.activeIndex ].classList.add( CLASS_ACTIVE_NAV );
      this._updateYearsVars( this.yearsEl.childNodes[ swiper.activeIndex ] );
    }
    if( this._config.type == 'contact' ){
      this._map({ target: this._element.querySelector( `[data-${PREFIX}-slide="${swiper.activeIndex}"]` ) });
      console.log( 'MySW: => _slideChange => activeIndex ' , swiper.activeIndex );
    }
    this._element.dispatchEvent( new CustomEvent( onSlideChange , { bubbles: true , cancelable: true , detail: { index: swiper.activeIndex } } ) );
  }

  _refresh( event ) {
    if ( this.yearsEl !== null && this.yearsEl.childNodes[ this._swiper.activeIndex ] ) {
      this._updateYearsVars( this.yearsEl.childNodes[ this._swiper.activeIndex ] );
    }
  }

  _nextSlide( event ) {
    this._swiper.slideNext( );
  }

  _prevSlide( event ) {
    this._swiper.slidePrev( );
  }

  _goToSlide( event ) {
    event.preventDefault( );
    const btn = event.currentTarget || event.target.closest( '.swiper-navigation-avatar-item' );
    const index = Number( btn?.dataset?.index );
    if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
  }

  _createAvatarNavigation( swiper ) {
    if ( this.swiperEl.querySelector( '.swiper-navigation-avatar' ) ) return;
    const frag = document.createDocumentFragment( );
    swiper.slides.forEach( ( el, index ) => {
      const url = el.getAttribute( SELECTOR_AVATAR );
      if ( !url ) return;
      const a = document.createElement( 'a' );
      a.classList.add( 'swiper-navigation-avatar-item' );
      if ( index == 0 ) a.classList.add( CLASS_ACTIVE_NAV );
      a.href = '#';
      a.dataset.index = String( index );
      a.style.backgroundImage = `url('${url}')`;
      frag.appendChild( a );
    } );
    if ( frag.childNodes.length == 0 ) return;
    this.avatarEl = document.createElement( 'nav' );
    this.avatarEl.className = 'swiper-navigation-avatar';
    this.avatarEl.appendChild( frag );
    this.avatarEl.addEventListener( 'click', ( ev ) => {
      const btn = ev.target.closest( '.swiper-navigation-avatar-item' );
      if ( !btn ) return;
      ev.preventDefault( );
      const index = Number( btn?.dataset?.index );
      if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
    }, false );
    this.swiperEl.prepend( this.avatarEl );
  }

  _createYearNavigation( swiper ) {
    if ( this.swiperEl.querySelector( '.swiper-navigation-year' ) ) return;
    const frag = document.createDocumentFragment( );
    swiper.slides.forEach( ( el, index ) => {
      const year = el.getAttribute( SELECTOR_YEAR );
      if ( !year ) return;
      const a = document.createElement( 'a' );
      a.classList.add( 'swiper-navigation-year-item' );
      if ( index == 0 ) a.classList.add( CLASS_ACTIVE_NAV );
      a.href = '#';
      a.dataset.index = String( index );
      a.textContent = year;
      frag.appendChild( a );
    } );
    if ( frag.childNodes.length == 0 ) return;
    this.yearsEl = document.createElement( 'nav' );
    this.yearsEl.className = 'swiper-navigation-year';
    this.yearsEl.appendChild( frag );
    this.yearsEl.addEventListener( 'click', ( ev ) => {
      const btn = ev.target.closest( '.swiper-navigation-year-item' );
      if ( !btn ) return;
      ev.preventDefault( );
      const index = Number( btn?.dataset?.index );
      if ( Number.isFinite( index ) ) this._swiper.slideTo( index );
    }, false );
    const wrapper = document.createElement( 'div' );
    wrapper.className = 'swiper-navigation-year-scroll';
    wrapper.appendChild( this.yearsEl );
    this.swiperEl.append( wrapper );
    this._updateYearsVars( this.yearsEl.childNodes[ 0 ] );
  }

  _updateYearsVars( btn ) {
    if ( !btn ) return;
    const rectBtn = btn.getBoundingClientRect( );
    const rectNav = this.yearsEl.getBoundingClientRect( );
    const left = rectBtn.left - rectNav.left;
    const width = rectBtn.width;
    this.yearsEl.style.setProperty( `--${PREFIX}-position-left`, `${left}px` );
    this.yearsEl.style.setProperty( `--${PREFIX}-size-width`, `${width}px` );
  }

  _map( event ) {
    console.log( 'MySW: => _map => event ' , event );
    const btn = event.target.closest( SELECTOR_SLIDE );
    if ( !btn || btn.classList.contains( CLASS_ACTIVE_NAV ) ) return;
    let active = this._element.querySelector( `${SELECTOR_SLIDE}.${CLASS_ACTIVE_NAV}` );
    if( active ){
      active.classList.remove( CLASS_ACTIVE_NAV );
    }
    btn.classList.add( CLASS_ACTIVE_NAV );
    window.dispatchEvent( new CustomEvent( onActive , {
      detail: { index: btn.dataset[ DATA_KEY_SLIDE ] }
    } ) );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ) {
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if ( el._myswInstance ) return;
      let cfg = {};
      const raw = el.getAttribute( SELECTOR );
      if ( raw ) {
        try {
          cfg = JSON.parse( raw );
        } catch ( e ) { cfg = {}; }
      }
      el._myswInstance = new MySW( el, cfg );
      instances.push( el._myswInstance );
    } );
    return instances;
  }
}

export default MySW;
